import type { CSSProperties } from "react";
import type { ConceptMetricColumn } from "./ConceptType";

/** 最多同时选择的概念数量 */
export const CONCEPT_MAX_SELECTION = 3;

/** 概念搜索页本地「最近选择」缓存（按 spaceProductId 隔离） */
export const CONCEPT_RECENT_CACHE = {
  version: 1,
  keyPrefix: "lf.conceptRecent",
  maxItems: 10,
} as const;

/** 概念搜索页「最近选择」列表样式配置 */
export interface ConceptSearchRecentConfig {
  /** 行高（px，min-height） */
  rowHeight: number;
  /** 概念名称字号（px） */
  nameFontSize: number;
  /** 概念编码字号（px） */
  codeFontSize: number;
}

export const CONCEPT_SEARCH_RECENT = {
  rowHeight: 38,
  nameFontSize: 14,
  codeFontSize: 12,
} as const satisfies ConceptSearchRecentConfig;

/** 股票列表右侧三列（最新价 / 涨跌幅 / 指标）布局配置 */
export interface ConceptListColumnsConfig {
  /** 最新价列宽（px） */
  priceWidth: number;
  /** 涨跌幅列宽（px） */
  pctWidth: number;
  /** 指标列宽（px，如基本面分、总市值等） */
  metricWidth: number;
  /** 三列之间的间距（px） */
  colGap: number;
  /** 最后一列（指标）数值距右边缘的内边距（px） */
  metricColPaddingRight: number;
}

export const CONCEPT_LIST_COLUMNS = {
  priceWidth: 56,
  pctWidth: 56,
  metricWidth: 64,
  colGap: 20,
  metricColPaddingRight: 5,
} as const satisfies ConceptListColumnsConfig;

/** 概念库主页布局间距配置 */
export interface ConceptPageLayoutConfig {
  /** 已选概念行与下方列表表头之间的间距（px） */
  selectedToListGap: number;
  /** 无已选概念时，列表表头距上方的间距（px） */
  listHeadTopGap: number;
  /** 股票行上下内边距（px），决定行与行之间的视觉间距 */
  stockRowPaddingY: number;
  /** 股票行左右内边距（px） */
  stockRowPaddingX: number;
}

export const CONCEPT_PAGE_LAYOUT = {
  selectedToListGap: 0,
  listHeadTopGap: 10,
  stockRowPaddingY: 7,
  stockRowPaddingX: 12,
} as const satisfies ConceptPageLayoutConfig;

/** 概念库空态与使用教程配置 */
export interface ConceptUsageGuideConfig {
  /** 「概念库怎么用」标题字号（px） */
  titleFontSize: number;
  /** 表头与「先选择概念后查看」之间的间距（px） */
  pickHintTopGap: number;
  /** 「先选择概念后查看」与下方「概念库怎么用」按钮之间的间距（px） */
  emptyToGuideGap: number;
}

export const CONCEPT_USAGE_GUIDE = {
  titleFontSize: 14,
  pickHintTopGap: 20,
  emptyToGuideGap: 20,
} as const satisfies ConceptUsageGuideConfig;

/**
 * 概念库金融字体（index.html 已预加载 JetBrains Mono；中文回退系统无衬线）
 */
export const CONCEPT_FONT_FAMILY =
  '"JetBrains Mono", "SFMono-Regular", ui-monospace, "Liberation Mono", Menlo, Consolas, -apple-system, BlinkMacSystemFont, "PingFang SC", "Helvetica Neue", sans-serif';

/** 股票名称颜色（粉色，参考雪球链接风格） */
export const CONCEPT_STOCK_NAME_COLOR = "#ec407a";

/** 股票名称字号（px） */
export const CONCEPT_STOCK_NAME_FONT_SIZE = 15;

/** 标题栏右侧搜索图标尺寸（px，宽高一致） */
export const CONCEPT_NAV_SEARCH_ICON_SIZE = 26;

/** 表头排序激活态小三角颜色 */
export const CONCEPT_SORT_ACTIVE_COLOR = CONCEPT_STOCK_NAME_COLOR;

/** 最后一列可循环切换的指标 */
export const CONCEPT_METRIC_COLUMNS: ConceptMetricColumn[] = [
  { field: "fundamentalScore", label: "基本面分" },
  { field: "positionScore", label: "位置分" },
  { field: "dividendYield", label: "股息率" },
  { field: "marketValue", label: "总市值" },
  { field: "floatMarketValue", label: "流通市值" },
  { field: "peRatio", label: "市盈率" },
];

export function conceptPageCssVars(
  columns: ConceptListColumnsConfig = CONCEPT_LIST_COLUMNS,
  layout: ConceptPageLayoutConfig = CONCEPT_PAGE_LAYOUT,
  guide: ConceptUsageGuideConfig = CONCEPT_USAGE_GUIDE
): CSSProperties {
  return {
    "--lf-concept-up": "#f54346",
    "--lf-concept-down": "#14b143",
    "--lf-concept-flat": "#333333",
    "--lf-concept-link": "#0067c0",
    "--lf-concept-border": "rgba(22, 24, 35, 0.08)",
    "--lf-concept-muted": "rgba(22, 24, 35, 0.45)",
    "--lf-concept-col-price-width": `${columns.priceWidth}px`,
    "--lf-concept-col-pct-width": `${columns.pctWidth}px`,
    "--lf-concept-col-metric-width": `${columns.metricWidth}px`,
    "--lf-concept-col-gap": `${columns.colGap}px`,
    "--lf-concept-metric-col-padding-right": `${columns.metricColPaddingRight}px`,
    "--lf-concept-selected-to-list-gap": `${layout.selectedToListGap}px`,
    "--lf-concept-list-head-top-gap": `${layout.listHeadTopGap}px`,
    "--lf-concept-stock-row-padding-y": `${layout.stockRowPaddingY}px`,
    "--lf-concept-stock-row-padding-x": `${layout.stockRowPaddingX}px`,
    "--lf-concept-font-family": CONCEPT_FONT_FAMILY,
    "--lf-concept-stock-name-color": CONCEPT_STOCK_NAME_COLOR,
    "--lf-concept-stock-name-font-size": `${CONCEPT_STOCK_NAME_FONT_SIZE}px`,
    "--lf-concept-nav-search-icon-size": `${CONCEPT_NAV_SEARCH_ICON_SIZE}px`,
    "--lf-concept-sort-active-color": CONCEPT_SORT_ACTIVE_COLOR,
    "--lf-concept-guide-title-font-size": `${guide.titleFontSize}px`,
    "--lf-concept-pick-hint-top-gap": `${guide.pickHintTopGap}px`,
    "--lf-concept-empty-to-guide-gap": `${guide.emptyToGuideGap}px`,
  } as CSSProperties;
}

export function conceptSearchCssVars(
  recent: ConceptSearchRecentConfig = CONCEPT_SEARCH_RECENT
): CSSProperties {
  return {
    "--lf-concept-font-family": CONCEPT_FONT_FAMILY,
    "--lf-concept-muted": "rgba(22, 24, 35, 0.45)",
    "--lf-concept-link": "#0067c0",
    "--lf-concept-search-recent-row-height": `${recent.rowHeight}px`,
    "--lf-concept-search-recent-name-font-size": `${recent.nameFontSize}px`,
    "--lf-concept-search-recent-code-font-size": `${recent.codeFontSize}px`,
  } as CSSProperties;
}
